import { useRef, useEffect, useCallback } from 'react';
import * as echarts from 'echarts';
import {
  TelemetryPanelConfig,
  TelemetryUnitSystem,
  TelemetryViewportState,
} from '../types/telemetry-dashboard.types';
import { isTelemetryChartViz } from '../utils/telemetry-color.utils';
import {
  optionFor,
  sparkOption,
  chartSig,
  multiChartSig,
  setPanelLegendSelected,
  clearPanelLegendSelected,
  attachAxisLabelTooltipHandler,
} from '../utils/telemetry-chart-options.utils';
import { updateGlobalTelemetrySeriesStore, getActiveTelemetrySeriesStore } from '../utils/telemetry-helpers.utils';

export function useTelemetryEChartsSync(
  layout: TelemetryPanelConfig[],
  timeframe: number,
  precision: number,
  units: TelemetryUnitSystem,
  viewport: TelemetryViewportState,
  gridElRef: React.RefObject<HTMLDivElement>,
  telemetrySeries?: any[],
  onViewportChange?: (patch: Partial<TelemetryViewportState>) => void
) {
  const ecInstances = useRef<Record<string, { ec: echarts.ECharts; node: HTMLElement; sig: string | null }>>({});
  const onViewportChangeRef = useRef(onViewportChange);
  onViewportChangeRef.current = onViewportChange;

  const handleDataZoom = useCallback((e: any) => {
    if (!onViewportChangeRef.current) return;
    const batch = e?.batch?.[0];
    let startMs: number | null = null;
    let endMs: number | null = null;

    if (batch) {
      if (typeof batch.startValue === 'number' && typeof batch.endValue === 'number') {
        startMs = batch.startValue;
        endMs = batch.endValue;
      }
    } else if (typeof e?.startValue === 'number' && typeof e?.endValue === 'number') {
      startMs = e.startValue;
      endMs = e.endValue;
    }

    if (startMs != null && endMs != null && endMs > startMs) {
      const spanMs = Math.max(5000, endMs - startMs);
      const store = getActiveTelemetrySeriesStore();
      let maxTs = 0;
      if (store && store.length > 0) {
        for (const s of store) {
          const last = s.data?.[s.data.length - 1]?.value?.[0];
          if (typeof last === 'number' && last > maxTs) maxTs = last;
        }
      }
      const refTs = maxTs > 0 ? maxTs : Date.now();
      const offsetMs = Math.max(0, refTs - endMs);

      onViewportChangeRef.current({ spanMs, offsetMs });

      // Automatically exit box-zoom mode across all chart instances
      Object.values(ecInstances.current).forEach((inst) => {
        try {
          inst.ec?.dispatchAction({
            type: 'takeGlobalCursor',
            key: 'dataZoomSelect',
            dataZoomSelectActive: false,
          });
        } catch (_) { }
      });

      if (typeof window !== 'undefined') {
        window.dispatchEvent(new CustomEvent('telemetry:zoom-deactivate'));
      }
    }
  }, []);

  const resizeAll = useCallback(() => {
    Object.values(ecInstances.current).forEach((o) => {
      try {
        if (o.ec) o.ec.resize();
      } catch (_) { }
    });
  }, []);

  const syncCharts = useCallback(() => {
    if (typeof echarts === 'undefined' || !gridElRef.current) return;

    // Eagerly sync the global telemetry store before computing chart signatures.
    // Guarded to only run when telemetrySeries contains valid data items to prevent wiping store.
    if (telemetrySeries && Array.isArray(telemetrySeries) && telemetrySeries.length > 0) {
      updateGlobalTelemetrySeriesStore(telemetrySeries);
    }

    const root = gridElRef.current;
    const seen: Record<string, boolean> = {};

    for (const w of layout) {
      const vizMode = w.graphMode;

      if (vizMode === 'kpi') {
        const k = `sp:${w.id}`;
        seen[k] = true;
        const node = root.querySelector(`[data-chart-id="${k}"]`) as HTMLElement;
        if (!node) continue;
        let o = ecInstances.current[k];
        if (!o || o.node !== node) {
          if (o && o.ec) o.ec.dispose();
          const ec = echarts.init(node, undefined, { renderer: 'canvas' });
          o = { ec, node, sig: null };
          ecInstances.current[k] = o;
        }
        const sig = chartSig(w as any, timeframe, precision, units as any, viewport);
        if (o.sig !== sig) {
          o.ec.setOption((sparkOption as any)(w as any, timeframe, units as any, viewport as any), true);
          o.sig = sig;
        }
      } else if (isTelemetryChartViz(vizMode)) {
        const k = w.id;
        seen[k] = true;
        const node = root.querySelector(`[data-chart-id="${w.id}"]`) as HTMLElement;
        if (!node) continue;
        let o = ecInstances.current[k];
        if (!o || o.node !== node) {
          if (o && o.ec) o.ec.dispose();
          const ec = echarts.init(node, undefined, { renderer: 'canvas' });
          ec.on('dataZoom', handleDataZoom);
          ec.on('legendselectchanged', (params: any) => {
            if (params?.selected) {
              setPanelLegendSelected(k, params.selected);
            }
          });
          attachAxisLabelTooltipHandler(ec);
          o = { ec, node, sig: null };
          ecInstances.current[k] = o;
        }
        const sig = vizMode === 'cchart'
          ? multiChartSig(w as any, precision, units as any, viewport)
          : chartSig(w as any, timeframe, precision, units as any, viewport);

        if (o.sig !== sig) {
          try {
            const currentOpt = o.ec.getOption() as any;
            const currentSel = currentOpt?.legend?.[0]?.selected;
            if (currentSel && Object.keys(currentSel).length > 0) {
              setPanelLegendSelected(k, currentSel);
            }
          } catch (_) { }

          o.ec.setOption(optionFor(w as any, timeframe, precision, units as any, viewport), true);
          o.ec.resize();
          o.sig = sig;
        }
      }
    }

    Object.keys(ecInstances.current).forEach((k) => {
      if (!seen[k]) {
        try {
          ecInstances.current[k].ec.dispose();
        } catch (_) { }
        delete ecInstances.current[k];
        clearPanelLegendSelected(k);
      }
    });
  }, [layout, timeframe, precision, units, viewport, gridElRef, telemetrySeries]);

  useEffect(() => {
    syncCharts();
    const timer = setTimeout(resizeAll, 100);
    const rAf = requestAnimationFrame(resizeAll);

    const handleWindowResize = () => {
      resizeAll();
      requestAnimationFrame(resizeAll);
    };
    window.addEventListener('resize', handleWindowResize);

    let ro: ResizeObserver | null = null;
    if (typeof ResizeObserver !== 'undefined') {
      ro = new ResizeObserver(() => {
        requestAnimationFrame(resizeAll);
      });
      if (gridElRef.current) {
        ro.observe(gridElRef.current);
      }
      Object.values(ecInstances.current).forEach((inst) => {
        if (inst.node) ro?.observe(inst.node);
      });
    }

    return () => {
      clearTimeout(timer);
      cancelAnimationFrame(rAf);
      window.removeEventListener('resize', handleWindowResize);
      if (ro) ro.disconnect();
    };
  }, [syncCharts, resizeAll, telemetrySeries, gridElRef]);

  return { ecInstances, syncCharts, resizeAll };
}
